import { useState, useCallback, useEffect, forwardRef } from 'react';
import { ArrowLeft, ArrowRight, Rocket, Zap, Info, Heart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EngiCoroLogo } from '@/components/workflow/EngiCoroLogo';
import { BRAND_NAVY, BRAND_SIDEBAR } from '@/constants/brandColors';
import { fireOnboardingCompleted } from '@/hooks/useOnboardingTelemetry';
import {
  WORKFLOW_TEMPLATES,
  ADVANCED_TEMPLATE_ID,
  getAiHoningPrompt,
  getBuildGuidance,
  WORKFLOW_PROFILE_KEY,
  type WorkflowProfile,
  type WorkflowTemplate,
} from '@/lib/workflowTemplates';
import ReactMarkdown from 'react-markdown';

// ---------------------------------------------------------------------------
// Tip Jar popup
// ---------------------------------------------------------------------------

function TipJarPopup({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70" style={{ contain: 'strict' }} onClick={onClose}>
      <div
        className="w-[min(90vw,400px)] rounded-xl border border-border bg-background shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/50">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-pink-500" />
            <h3 className="text-sm font-semibold">Support EngiCoro</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground text-center leading-relaxed">
            EngiCoro Flow Weaver is built with passion. If you find it useful,
            consider supporting the project!
          </p>

          {/* PayPal QR Code */}
          <div className="flex flex-col items-center gap-3">
            <img
              src="/paypal-qr.png"
              alt="PayPal QR Code — Scan to tip"
              className="w-48 h-48 rounded-lg border border-border object-contain"
            />
            <p className="text-xs text-muted-foreground">Scan with your phone to tip via PayPal</p>
          </div>

          {/* PayPal link button */}
          <a
            href="https://paypal.me/arentb"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-all group"
          >
            <div className="w-10 h-10 rounded-lg bg-[#0070ba] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs">PP</span>
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-semibold group-hover:text-primary transition-colors">PayPal</h4>
              <p className="text-[11px] text-muted-foreground">Or click here to open PayPal</p>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors ml-auto flex-shrink-0" />
          </a>

          <p className="text-[10px] text-muted-foreground text-center">
            Thank you for your generosity!
          </p>
        </div>
      </div>
    </div>
  );
}

interface WorkflowWizardDialogProps {
  open: boolean;
  onComplete: (profile: WorkflowProfile | null) => void;
}

type Step = 'choose' | 'q1' | 'q2' | 'guidance';

const REMEMBER_KEY = 'engicoro-wizard-remember';

export function WorkflowWizardDialog({ open, onComplete }: WorkflowWizardDialogProps) {
  const [step, setStep] = useState<Step>('choose');
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [q1Answer, setQ1Answer] = useState('');
  const [q2Answer, setQ2Answer] = useState('');
  const [tipJarOpen, setTipJarOpen] = useState(false);
  const [remember, setRemember] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      return raw === null ? true : raw === 'true';
    } catch { return true; }
  });

  useEffect(() => {
    try { localStorage.setItem(REMEMBER_KEY, String(remember)); } catch { /* ignore */ }
  }, [remember]);

  // När wizard:n re-opens (via TopBar:s "Wizard"-knapp) — reset:a state
  // till 'choose'-steget så user inte landar i mitten av föregående
  // session. Idempotent vid första mount (open=true initialt).
  useEffect(() => {
    if (open) {
      setStep('choose');
      setSelectedTemplate(null);
      setQ1Answer('');
      setQ2Answer('');
    }
  }, [open]);

  const handleSelectTemplate = useCallback((tpl: WorkflowTemplate) => {
    setSelectedTemplate(tpl);
    setQ1Answer('');
    setQ2Answer('');
    setStep('q1');
  }, []);

  const handleAdvanced = useCallback(() => {
    const profile: WorkflowProfile = {
      templateId: ADVANCED_TEMPLATE_ID,
      q1: '',
      q2: '',
      aiHoningPrompt: '',
      guidance: '',
    };
    if (remember) {
      localStorage.setItem(WORKFLOW_PROFILE_KEY, JSON.stringify(profile));
    } else {
      try { localStorage.removeItem(WORKFLOW_PROFILE_KEY); } catch { /* ignore */ }
    }
    onComplete(profile);
  }, [onComplete, remember]);

  const handleQ1 = useCallback((answerId: string) => {
    setQ1Answer(answerId);
    setStep('q2');
  }, []);

  const handleQ2 = useCallback((answerId: string) => {
    setQ2Answer(answerId);
    setStep('guidance');
  }, []);

  const handleFinish = useCallback(() => {
    if (!selectedTemplate) return;
    const profile: WorkflowProfile = {
      templateId: selectedTemplate.id,
      q1: q1Answer,
      q2: q2Answer,
      aiHoningPrompt: getAiHoningPrompt(selectedTemplate.id, q1Answer, q2Answer),
      guidance: getBuildGuidance(selectedTemplate.id, q1Answer, q2Answer),
    };
    if (remember) {
      localStorage.setItem(WORKFLOW_PROFILE_KEY, JSON.stringify(profile));
    } else {
      try { localStorage.removeItem(WORKFLOW_PROFILE_KEY); } catch { /* ignore */ }
    }
    fireOnboardingCompleted({ templateId: selectedTemplate.id });
    onComplete(profile);
  }, [selectedTemplate, q1Answer, q2Answer, onComplete, remember]);

  const handleBack = useCallback(() => {
    if (step === 'q1') { setStep('choose'); setSelectedTemplate(null); }
    else if (step === 'q2') { setStep('q1'); setQ2Answer(''); }
    else if (step === 'guidance') setStep('q2');
  }, [step]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" style={{ contain: 'strict' }} data-testid="workflow-wizard">
      <div className="w-[min(95vw,720px)] max-h-[85vh] flex flex-col rounded-xl border border-border bg-background shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border" style={{ background: BRAND_SIDEBAR }}>
          <EngiCoroLogo className="h-16 flex-shrink-0" />
          <p className="text-[11px]" style={{ color: BRAND_NAVY, opacity: 0.5 }}>Choose your workflow type to get started</p>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-6">
            {/* Step: Choose template */}
            {step === 'choose' && (
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold mb-1">What are you building?</h2>
                  <p className="text-sm text-muted-foreground">Select a workflow type and we&apos;ll tailor the experience for you.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {WORKFLOW_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => handleSelectTemplate(tpl)}
                      className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
                      data-testid={`wizard-template-${tpl.id}`}
                    >
                      <span className="text-2xl flex-shrink-0 mt-0.5">{tpl.icon}</span>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">{tpl.title}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{tpl.subtitle}</p>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Advanced option */}
                <div className="pt-3 border-t border-border mt-4">
                  <button
                    onClick={handleAdvanced}
                    className="w-full flex items-center gap-3 p-4 rounded-lg border border-dashed border-border hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
                    data-testid="wizard-advanced"
                  >
                    <Zap className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">Advanced — Blank Canvas</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">Skip guidance. You know what you're doing.</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step: Question 1 */}
            {step === 'q1' && selectedTemplate && (
              <QuestionStep
                template={selectedTemplate}
                questionIndex={0}
                onSelect={handleQ1}
                onBack={handleBack}
                stepLabel="Step 1 of 2"
              />
            )}

            {/* Step: Question 2 */}
            {step === 'q2' && selectedTemplate && (
              <QuestionStep
                template={selectedTemplate}
                questionIndex={1}
                onSelect={handleQ2}
                onBack={handleBack}
                stepLabel="Step 2 of 2"
              />
            )}

            {/* Step: Guidance */}
            {step === 'guidance' && selectedTemplate && (
              <div className="space-y-5">
                <button onClick={handleBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>

                <div className="text-center">
                  <span className="text-3xl">{selectedTemplate.icon}</span>
                  <h2 className="text-lg font-bold mt-2">{selectedTemplate.title}</h2>
                  <p className="text-xs text-muted-foreground mt-1">Here's how to build this in Flow Weaver</p>
                </div>

                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-4 h-4 text-primary" />
                    <h3 className="text-sm font-semibold">Build Guide</h3>
                  </div>
                  <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed">
                    <ReactMarkdown>{getBuildGuidance(selectedTemplate.id, q1Answer, q2Answer)}</ReactMarkdown>
                  </div>
                </div>

                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Rocket className="w-4 h-4 text-primary" />
                    <h3 className="text-sm font-semibold text-primary">AI Assistant is tuned</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    The AI assistant has been configured for <strong>{selectedTemplate.title}</strong> workflows.
                    Open the AI Assistant and describe what you need — it will generate blocks, connectors, and groups
                    optimized for this type of diagram.
                  </p>
                </div>

                <Button onClick={handleFinish} className="w-full gap-2" size="lg" data-testid="wizard-start">
                  <Rocket className="w-4 h-4" />
                  Start Building
                </Button>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer with remember-checkbox + tip jar */}
        <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-border bg-muted/30">
          <label className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-border accent-primary cursor-pointer"
              data-testid="wizard-remember-choices"
            />
            Remember my choices
          </label>
          <button
            onClick={() => setTipJarOpen(true)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-pink-500 transition-colors"
          >
            <Heart className="w-3.5 h-3.5" />
            Support the project
          </button>
        </div>
      </div>

      <TipJarPopup open={tipJarOpen} onClose={() => setTipJarOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: Question step
// ---------------------------------------------------------------------------

interface QuestionStepProps {
  template: WorkflowTemplate;
  questionIndex: 0 | 1;
  onSelect: (answerId: string) => void;
  onBack: () => void;
  stepLabel: string;
}

const QuestionStep = forwardRef<HTMLDivElement, QuestionStepProps>(function QuestionStep(
  { template, questionIndex, onSelect, onBack, stepLabel },
  ref,
) {
  const q = template.questions[questionIndex];

  return (
    <div ref={ref} className="space-y-5">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <span className="text-[11px] text-muted-foreground font-medium">{stepLabel}</span>
      </div>

      <div className="text-center">
        <span className="text-2xl">{template.icon}</span>
        <h2 className="text-lg font-bold mt-2">{q.question}</h2>
        <p className="text-xs text-muted-foreground mt-1">{template.title}</p>
      </div>

      <div className="space-y-3">
        {q.options.map((opt) => (
          <button
            key={opt.id}
            onClick={() => onSelect(opt.id)}
            className="w-full flex items-start gap-3 p-4 rounded-lg border border-border bg-card hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
            data-testid={`wizard-option-${opt.id}`}
          >
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h3 className="font-semibold text-sm group-hover:text-primary transition-colors">{opt.label}</h3>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{opt.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
});
